import React, { FC, useEffect, useRef, useState } from 'react';
import { IconSeach } from '../Icons/IconSeach';
import { IconTools } from '../Icons/IconTools';
import { IconDropDownArrowRounded } from '../Icons';
import IconClose from '../Icons/IconClose';
import './Header.css';
import HeaderNavigator from '../HeaderNavigator/HeaderNavigator';
import HeaderNavigatorMobile from '../HeaderNavigatorMobile/HeaderNavigatorMobile';
import { LangDropdown } from '../LangDropdown/LangDropdown';
import IconFlagEs from '../Icons/IconFlagEs';
import IconFlagEn from '../Icons/IconFlagEn';
import IconFlagHi from '../Icons/IconFlagHi';
import { IconSuscripciones } from '../Icons/IconSuscripciones';
import { IconLogoMobile } from '../Icons/IconLogoMobile';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { postApiNews } from '../../api';
import { NewsResponseViewModel } from '../../api/models/newsResponseViewModel';

const Header: FC<{ children: React.ReactNode }> = ({ children }) => {
	const { t, i18n } = useTranslation();
	const [isHeaderNavigatorMobileOpen, setIsHeaderNavigatorMobileOpen] = useState(false);
	const { state, dispatch } = useAuth();
	const userFullName = localStorage.getItem('alias');
	const userEmail = localStorage.getItem('username');
	const navigate = useNavigate();

	const [searchTerm, setSearchTerm] = useState('');
	const [isActiveSearch, setIsActiveSearch] = useState(false);
	const [isScrolled, setIsScrolled] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [dataNewsList, setDataNewsList] = useState<NewsResponseViewModel[]>([]);
	const inputRef = useRef<HTMLInputElement>(null);

	const toggleHeaderNavigatorMobile = () => {
		setIsHeaderNavigatorMobileOpen(!isHeaderNavigatorMobileOpen);
	};

	const obtenerNoticias = async () => {
		setIsLoading(true);
		const response = await postApiNews({
			page: 1,
			size: 100,
		});
		setDataNewsList([...(response.results ?? [])]);
		setIsLoading(false);
	};

	const handleChange = (e: { target: { value: any } }) => {
		const value = e.target.value;
		setSearchTerm(value);
		if (value.length >= 3) {
			obtenerNoticias();
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && searchTerm.length >= 3) {
			navigate(`/search?query=${encodeURIComponent(searchTerm)}`);
			setSearchTerm('');
		}
	};

	const handleSubmit = () => {
		navigate(`/search?query=${encodeURIComponent(searchTerm)}`);
		setSearchTerm('');
	};

	const filteredNews =
		searchTerm.length >= 3
			? dataNewsList?.filter((news) => news?.title?.toLowerCase().includes(searchTerm.toLowerCase())) || []
			: [];

	const handleClickNews = (id: any) => {
		setSearchTerm('');
		navigate(`/noticia/${id}`);
	};

	const toggleActiveSearch = () => {
		setIsActiveSearch(!isActiveSearch);
	};

	useEffect(() => {
		if (isActiveSearch && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isActiveSearch]);

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 10);
		};

		handleScroll();
		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const handleLogIn = () => {
		navigate('/login');
	};

	const handleLogout = () => {
		dispatch({ type: 'LOGOUT' });
		localStorage.removeItem('token');
		localStorage.removeItem('refreshToken');
		localStorage.removeItem('personalInformation');
		navigate('/login');
	};

	return (
		<>
			<div
				className={`header ${state.isAuthenticated ? 'authenticated' : 'not-authenticated'} ${isScrolled ? 'scrolled' : ''}`}>
				{state.isAuthenticated && (
					<>
						<div className="header__left">
							<div
								className="header__logo"
								onClick={toggleHeaderNavigatorMobile}>
								<IconLogoMobile />
								<IconDropDownArrowRounded />
							</div>
							{children && <div className="header__info">{children}</div>}
						</div>
						<div className="header__right">
							<div
								className="header__search__toggle"
								onClick={toggleActiveSearch}>
								<IconSeach />
							</div>
							<div className={`header__search ${isActiveSearch ? 'active' : ''}`}>
								<input
									type="text"
									ref={inputRef}
									className="buscar-noticia header__search__input"
									placeholder={t('header.search.placeholder')}
									value={searchTerm}
									onChange={handleChange}
									onKeyDown={handleKeyDown}
								/>
								{filteredNews.length > 0 && (
									<ul className="filter-search">
										{filteredNews.map((news) => (
											<li
												key={news.id}
												className="filter-search__item"
												onClick={() => handleClickNews(news.id)}>
												<div className="filter-search__figure">
													<img
														src={news.image || ''}
														alt={news.title || ''}
													/>
												</div>
												<div className="filter-search__data">
													<h2 className="search__title">{news.title}</h2>
												</div>
											</li>
										))}
									</ul>
								)}
								<button
									type="button"
									className="header__search__button"
									onClick={handleSubmit}>
									<IconSeach />
								</button>
								<button
									type="button"
									className="header__search__close"
									onClick={toggleActiveSearch}>
									<IconClose fill="#364651" />
								</button>
							</div>

							<div className="header__tools">
								<div className="header__tools__text">{t('header.account')}</div>

								<div className="header__tools__block">
									<IconTools />
								</div>

								<div className="header__tools__account">
									<span className="header__tools__account__text">{userFullName?.charAt(0)}</span>
									<IconDropDownArrowRounded className="header__tools__account__icon" />

									<div className="header__account__menu">
										<div className="header__account__menu__container">
											<div className="header__account__menu__top">
												<div className="header__account__menu__avatar">{userFullName?.charAt(0)}</div>
												<div className="header__account__menu__lang">
													<button
														onClick={() => i18n.changeLanguage('es')}
														className={i18n.language === 'es' ? 'btnlang-selected btnlang' : 'btnlang'}>
														<IconFlagEs />
													</button>
													<button
														onClick={() => i18n.changeLanguage('en')}
														className={i18n.language === 'en' ? 'btnlang-selected btnlang' : 'btnlang'}>
														<IconFlagEn />
													</button>
													<button
														onClick={() => i18n.changeLanguage('hi')}
														className={i18n.language === 'hi' ? 'btnlang-selected btnlang' : 'btnlang'}>
														<IconFlagHi />
													</button>
												</div>
											</div>
											<div className="header__account__menu__info">
												<span className="header__account__menu__info__name">{userFullName}</span>
												<span className="header__account__menu__info__email">{userEmail}</span>
											</div>
											<div className="header__account__menu__items">
												<Link
													className="header__account__menu__item"
													to="/suscripciones">
													<IconSuscripciones />
													<span className="header__account__menu__item__text">{t('header.profile.packs')}</span>
												</Link>
											</div>
											<div className="header__account__menu__bottom">
												<span
													className="header__account__menu__bottom__logout"
													onClick={handleLogout}>
													{t('header.profile.logout')}
												</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</>
				)}
				{/* No logued */}
				{!state.isAuthenticated && (
					<>
						<div className="header__left">
							<div
								className="header__logo"
								onClick={toggleHeaderNavigatorMobile}>
								<IconLogoMobile />
								<IconDropDownArrowRounded />
							</div>
							{children && <div className="header__info">{children}</div>}
						</div>
						<div className="navbar__right">
							<div className="d-flex">
								<div
									className="header__btn-login"
									onClick={handleLogIn}>
									<div>{t('header.login')}</div>
								</div>
							</div>

							<LangDropdown />
						</div>
					</>
				)}
			</div>
			<HeaderNavigator />
			{isHeaderNavigatorMobileOpen && <HeaderNavigatorMobile onClose={() => setIsHeaderNavigatorMobileOpen(false)} />}

			{children && <div className="hero_top_mobile">{children}</div>}
		</>
	);
};

export default Header;
